import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import { Badge, Form } from "react-bootstrap";
import { VideoTag } from "../../interfaces/video";

interface VideoTagFilterProps {
    tags: VideoTag[];
    selectedTagIds: number[];
    onChange: (selectedIds: number[]) => void;
}

export function VideoTagFilter({ tags, selectedTagIds, onChange }: VideoTagFilterProps) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);

    const filteredTags = search
        ? tags.filter(tag => tag.name.toLowerCase().includes(search.toLowerCase()))
        : tags;

    const selectedTags = tags.filter(tag => selectedTagIds.includes(tag.id));

    const handleToggle = (tagId: number) => {
        if (selectedTagIds.includes(tagId)) {
            onChange(selectedTagIds.filter(id => id !== tagId));
        } else {
            onChange([...selectedTagIds, tagId]);
        }
    };

    const handleRemoveTag = (tagId: number) => {
        onChange(selectedTagIds.filter(id => id !== tagId));
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="video-tag-filter" ref={containerRef}>
            <div className="video-tag-filter-field" onClick={() => setOpen(!open)}>
                {selectedTags.length === 0 &&
                    <span className="text-muted">{t("Filter by tags")}</span>
                }
                {selectedTags.map(tag => (
                    <Badge key={tag.id} bg="secondary" className="me-1">
                        {tag.name}
                        <span className="ms-1" role="button" onClick={(e) => { e.stopPropagation(); handleRemoveTag(tag.id); }}>&times;</span>
                    </Badge>
                ))}
                <i className="bi bi-chevron-down video-tag-filter-icon"></i>
            </div>
            {open &&
                <div className="video-tag-filter-dropdown">
                    <Form.Control
                        size="sm"
                        type="text"
                        placeholder={t("Search tags")}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="mb-2"
                        autoFocus
                    />
                    <div className="video-tag-filter-list">
                        {filteredTags.map(tag => (
                            <Form.Check
                                key={tag.id}
                                type="checkbox"
                                id={`filter-tag-${tag.id}`}
                                label={tag.name}
                                checked={selectedTagIds.includes(tag.id)}
                                onChange={() => handleToggle(tag.id)}
                            />
                        ))}
                        {filteredTags.length === 0 &&
                            <div className="text-muted small">{t("No tags found")}</div>
                        }
                    </div>
                </div>
            }
        </div>
    );
}
